import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

export const SUPPORTED_LANGUAGES = ['de', 'en'];
export const DEFAULT_LANGUAGE = 'de';

/**
 * Best-effort language detection when the URL has no prefix.
 * Priority: previous choice (localStorage) > browser languages > default.
 */
export function detectPreferredLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = localStorage.getItem('e-matrix-lang');
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  const navLangs = navigator.languages || [navigator.language || ''];
  for (const raw of navLangs) {
    const primary = (raw || '').toLowerCase().split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(primary)) return primary;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Extract the language prefix from a pathname.
 * Returns e.g. { lang: 'de', rest: '/about' } or { lang: null, rest: '/about' }.
 */
export function splitLangFromPath(pathname) {
  const match = pathname.match(/^\/(de|en)(\/|$)(.*)$/);
  if (match) {
    return { lang: match[1], rest: '/' + (match[3] || '') };
  }
  return { lang: null, rest: pathname };
}

export const LanguageProvider = ({ children }) => {
  // The URL is the source of truth for language.
  const [language, setLanguageState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    const { lang } = splitLangFromPath(window.location.pathname);
    return lang || detectPreferredLanguage();
  });

  // Listen for URL changes (browser back/forward) and re-derive language.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = () => {
      const { lang } = splitLangFromPath(window.location.pathname);
      if (lang && lang !== language) setLanguageState(lang);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [language, setLanguageState]);

  // Keep html[lang], localStorage in sync.
  useEffect(() => {
    try {
      localStorage.setItem('e-matrix-lang', language);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('Failed to save language preference:', error);
      }
    }
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(`Translation missing for key: ${key}`);
        }
        return key;
      }
    }
    return value;
  }, [language]);

  /**
   * Navigate to the SAME page in the other language (hard navigation so the
   * BrowserRouter remounts with the new basename). Old unprefixed URLs get
   * prefixed with the requested language.
   */
  const setLanguage = useCallback((next) => {
    if (typeof window === 'undefined' || !SUPPORTED_LANGUAGES.includes(next)) return;
    const { rest } = splitLangFromPath(window.location.pathname);
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    const newPath = `/${next}${rest === '/' ? '' : rest}${search}${hash}` || `/${next}`;
    window.location.assign(newPath);
  }, []); // Stable: uses only window globals and constants

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'de' ? 'en' : 'de');
  }, [language, setLanguage]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
